import React, { useCallback, useEffect, useRef, useState } from 'react';
import './DietDiary.scss';
import TableHeader from '../../BaseComponents/TableHeader';
import { getDietDiaryList } from '../../../api/dietDiary';
import { useDispatch, useSelector } from 'react-redux';
import {
  appendDietDiaryList,
  setMemberDietDiary,
} from '../../../store/memberSlice';
import DietDiaryTableRow from './DietDiaryTableRow';
import { RootState } from '../../../store/store';

type indexProps = {};

const headerColumns = [
  { title: 'Date', key: 'date', minWidth: '104px', flex: 0 },
  { title: 'Time', key: 'time', minWidth: '104px', flex: 0 },
  {
    title: 'Meal type',
    key: 'mealType',
    minWidth: '104px',
    flex: 0,
  },
  { title: 'Note', key: 'note', minWidth: '200px', flex: 1 },
];

const DietDiary: React.FC<indexProps> = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<any | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef<number>(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { dietDiary, limit } = useSelector((state: RootState) => state.member);

  const loadMoreDietRecords = useCallback(async () => {
    if (isLoading || !hasMore) return;
    
    setIsLoading(true);
    offsetRef.current += limit;
    try {
      const dietListResp = await getDietDiaryList(
        window.location.pathname.split('/')[2],
        offsetRef.current,
        limit
      );
      
      if (dietListResp.diet_records.length > 0) {
        dispatch(appendDietDiaryList(dietListResp.diet_records));
        setHasMore(dietListResp.diet_records.length >= limit);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('fetchDietRecords error :>> ', error);
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, limit, isLoading, hasMore]);

  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (!scrollContainerRef.current || isLoading || !hasMore) return;
      
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const threshold = 100;
      
      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        loadMoreDietRecords();
      }
    }, 150);
  }, [isLoading, hasMore, loadMoreDietRecords]);

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    }

    const fetchMember = async () => {
      try {
        setIsLoading(true);
        const resp = await getDietDiaryList(
          window.location.pathname.split('/')[2]
        );
        dispatch(setMemberDietDiary(resp.diet_records));
      } catch (error) {
        setError(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMember();

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll, dispatch]);

  if (error) return <div>Error loading diet diary</div>;

  return (
    <div className='diet-container'>
      <div className='diet-header-row'>
        <div></div>
      </div>
      <div className='scroll-container diet-table'>
        <div className='table-container'>
          <TableHeader headerColumns={headerColumns} gap='40px' />
          <div className='diet-table-body' ref={scrollContainerRef}>
            {dietDiary?.map((dietRecord, index) => (
              <DietDiaryTableRow
                dietRecord={dietRecord}
                key={`${dietRecord.id}-${index}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DietDiary;
